const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
const NAME_MAX = 80;

const isHexColor = (value) => typeof value === "string" && HEX_COLOR.test(value);

const clampNumber = (value, min, max, fallback) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return fallback;
    }
    return Math.min(max, Math.max(min, number));
};

export function validatePresetName(name, presets, exceptId = null) {
    const trimmed = String(name || "").trim();
    if (!trimmed) {
        return "Preset name is required.";
    }
    if (trimmed.length > NAME_MAX) {
        return `Preset name must be ${NAME_MAX} characters or fewer.`;
    }
    const duplicate = (presets || []).some((preset) => (
        preset.id !== exceptId && String(preset.name || "").trim().toLowerCase() === trimmed.toLowerCase()
    ));
    if (duplicate) {
        return "A preset with this name already exists.";
    }
    return "";
}

export function captureGraphPreset(charts, appearance, layout) {
    const selected = (charts || []).filter((chart) => chart?.tagId);
    if (selected.length === 0) {
        return { ok: false, error: "Select at least one tag." };
    }
    const tags = [];
    const seen = new Set();
    for (const chart of selected) {
        const tagId = Number(chart.tagId);
        if (!Number.isInteger(tagId) || tagId < 1) {
            return { ok: false, error: "A selected tag is invalid." };
        }
        if (seen.has(tagId)) {
            return { ok: false, error: "Each tag can appear once in a preset." };
        }
        seen.add(tagId);
        const custom = Boolean(chart.colorCustom) && isHexColor(chart.color);
        tags.push({
            tag_id: tagId,
            order: tags.length,
            color: custom ? chart.color : null,
            color_custom: custom,
            axis: chart.yAxis === "y1" ? "RIGHT" : "LEFT",
            visible: chart.visible !== false,
        });
    }
    return {
        ok: true,
        configuration: {
            version: 1,
            tags,
            display: {
                chart_mode: layout === "combined" ? "combined" : "separate",
                grid_color: isHexColor(appearance?.gridColor) ? appearance.gridColor : null,
                grid_opacity: appearance?.gridOpacity == null
                    ? null
                    : clampNumber(appearance.gridOpacity, 0.15, 1, 0.55),
                line_width: clampNumber(appearance?.lineWidth, 1, 6, 2.5),
                show_points: appearance?.showPoints !== false,
                point_size: Math.round(clampNumber(appearance?.pointSize, 0, 10, 3)),
            },
        },
    };
}

export function restoreGraphPreset(configuration, availableTags, idFactory) {
    if (!configuration || configuration.version !== 1 || !Array.isArray(configuration.tags)) {
        return { ok: false, error: "This preset could not be read." };
    }
    const available = new Map((availableTags || []).map((tag) => [Number(tag.id), tag]));
    const ordered = configuration.tags
        .map((item, index) => ({ item, index }))
        .sort((left, right) => {
            const leftOrder = Number.isInteger(left.item?.order) ? left.item.order : left.index;
            const rightOrder = Number.isInteger(right.item?.order) ? right.item.order : right.index;
            return leftOrder - rightOrder;
        });
    const charts = [];
    const skipped = [];
    const seen = new Set();
    ordered.forEach(({ item }) => {
        const tagId = Number(item?.tag_id);
        if (!Number.isInteger(tagId) || seen.has(tagId)) {
            return;
        }
        seen.add(tagId);
        const tag = available.get(tagId);
        if (!tag) {
            skipped.push(tagId);
            return;
        }
        const custom = Boolean(item.color_custom) && isHexColor(item.color);
        charts.push({
            id: idFactory(),
            tagId: String(tag.id),
            tagName: tag.name || "",
            signalType: tag.signal_type || "",
            opcDataType: tag.opc_data_type || "",
            chartData: null,
            tableData: [],
            statusEvents: [],
            chartNote: "",
            color: custom ? item.color : "",
            colorCustom: custom,
            visible: item.visible !== false,
            yAxis: item.axis === "RIGHT" ? "y1" : "y",
        });
    });
    if (charts.length === 0) {
        return {
            ok: false,
            error: "Every saved tag is unavailable. The current graph was left unchanged.",
            skipped,
        };
    }
    const display = configuration.display || {};
    return {
        ok: true,
        charts,
        layout: display.chart_mode === "combined" ? "combined" : "separate",
        appearancePatch: {
            gridColor: isHexColor(display.grid_color) ? display.grid_color : null,
            gridOpacity: display.grid_opacity == null ? null : clampNumber(display.grid_opacity, 0.15, 1, null),
            lineWidth: clampNumber(display.line_width, 1, 6, 2.5),
            showPoints: display.show_points !== false,
            pointSize: Math.round(clampNumber(display.point_size, 0, 10, 3)),
        },
        skipped,
        warning: skipped.length ? `Skipped unavailable tags: ${skipped.join(", ")}.` : "",
    };
}

export function presetApplication(currentAppearance, restored) {
    const selectorStyles = restored.charts.map((chart) => (
        chart.colorCustom && isHexColor(chart.color)
            ? { color: chart.color, colorCustom: true }
            : { color: null, colorCustom: false }
    ));
    return {
        charts: restored.charts,
        layout: restored.layout,
        appearance: {
            ...currentAppearance,
            ...restored.appearancePatch,
            selectorStyles,
        },
    };
}

export function presetApiDetail(error, fallback) {
    const detail = error?.response?.data?.detail;
    return typeof detail === "string" && detail ? detail : fallback;
}
